import PropTypes from "prop-types";

/**
 * RealisticChiller – High-fidelity 3D Industrial SCADA Chiller component.
 * Features:
 * - Authentic 3D rendered Chiller.png base model (tightly cropped 541x351)
 * - Exact vector pilot lamps from Chiller.svg mapped with pixel-precision onto cabinet door
 * - Animated mini LCD cooling wave activity without static numerical values
 * - Red hot water inflow line (within upper inlet pipe at Y: 259)
 * - Blue chilled water outflow line (within lower outlet pipe at Y: 307.5)
 * - Continuous internal shell-and-tube chilling circuit through the evaporator drum
 * - Top compressor refrigerant suction and discharge flow loop
 * - Speed scales dynamically with telemetry/power (speedScale)
 * - 100% photo-realistic appearance in dark and light modes
 */
const RealisticChiller = ({
  isRunning,
  isOffline,
  statusColor,
  speedScale = 1,
  opacity = 1,
  width = "100%",
  height = "100%",
}) => {
  const isAlarm = statusColor === "red" && !isOffline;
  const flowDuration = isRunning ? `${Math.max(0.3, 1.2 / (speedScale || 1))}s` : "0s";
  const waveDuration = isRunning ? `${Math.max(0.4, 1.6 / (speedScale || 1))}s` : "0s";

  return (
    <div
      className={`scada-realistic-container ${isOffline ? "scada-realistic--offline" : ""}`}
      style={{
        width,
        height,
        opacity,
      }}
    >
      {/* 3D Realistic Base Image */}
      <img
        src="/scada-realistic/Chiller.png"
        alt="SCADA Realistic Chiller"
        className="scada-realistic-img"
      />

      {/* Precision Dynamic SVG Overlay (Synchronized in 541x351 coordinate space) */}
      <svg
        className="scada-realistic-overlay"
        viewBox="0 0 541 351"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          {/* Clip path precisely covering the mini LCD screen on the right cabinet door */}
          <clipPath id="chillerLcdClip">
            <rect x="175.5" y="49.5" width="20" height="14.5" rx="1" />
          </clipPath>

          {/* Linear Gradients for Flow Streams */}
          <linearGradient id="chillerHotFlowGrad" x1="100%" y1="0%" x2="0%" y2="0%">
            <stop offset="0%" stopColor="#ff6b6b" stopOpacity="0.5" />
            <stop offset="30%" stopColor="#ff3333" stopOpacity="1" />
            <stop offset="100%" stopColor="#ef4444" stopOpacity="1" />
          </linearGradient>

          <linearGradient id="chillerColdFlowGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#0ea5e9" stopOpacity="1" />
            <stop offset="70%" stopColor="#38bdf8" stopOpacity="1" />
            <stop offset="100%" stopColor="#bae6fd" stopOpacity="0.5" />
          </linearGradient>

          {/* Continuous Internal Thermal Chilling Loop Gradient */}
          <linearGradient id="chillerThermalLoopGrad" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#f87171" stopOpacity="0.9" />
            <stop offset="35%" stopColor="#fb923c" stopOpacity="0.85" />
            <stop offset="60%" stopColor="#38bdf8" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#0284c7" stopOpacity="0.9" />
          </linearGradient>

          <filter id="chillerFlowGlow" x="-20%" y="-50%" width="140%" height="200%">
            <feGaussianBlur stdDeviation="1.5" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {/* ═══════════════════════════════════════════════════════════════
            1. Control Cabinet Pilot Lamps (Exact Vector Geometry from Chiller.svg)
            ═══════════════════════════════════════════════════════════════ */}
        {/* ── Green RUN Pilot Lamp (from Chiller.svg lines 1419-1425) ── */}
        <g
          transform="translate(-81.4583, -57.4583) scale(0.416667)"
          style={{
            animation: isRunning ? "scada-led-blink 1.8s infinite ease-in-out" : "none",
            opacity: isRunning ? 1 : 0.4,
          }}
        >
          <path
            fill="#22631f"
            d="M467.62,279.54c2.28,2.22-.32,10.95-3.51,11.52-3.66,6.16-12.16,7.81-17.74,4.67-6.66-3.75-9.2-10.52-6.58-17.95,2.2-6.25,8.78-9.31,15.27-8.56,5.74.66,10.59,4.45,12.55,10.32Z"
          />
          <path
            fill="#a0a5ac"
            d="M470.49,281.64c-1.96,4.09-.59,5.9-5.01,11.62-.1-1.02-.42-1.85-1.36-2.19,1.99-3.35,2.76-7.62,3.51-11.52,1.04-5.5-4.62-8.54-8.83-12.41,7.12,1.32,10.96,8.24,11.7,14.51Z"
          />
          <path
            fill={isRunning ? "#33be10" : "#1a4d18"}
            d="M462.71,278.58c3.69,4.91,1.71,12.1-3.7,14.9-1.45-5.64-10.6-4.43-12.04.65-6.23-2.44-8.1-14.49-2.29-18.06,2.06.85,4.2,1.75,6.45,2.69l4.94-.11c2.87-1.3,5.08-1.33,6.65-.07Z"
          />
          <path
            fill={isRunning ? "#a7db6e" : "#2d5e23"}
            d="M462.71,278.58c-7.11,3.84-17.33,3.15-18.04-2.51,3.22-1.98,6.33-4.18,10.21-3.39,3.39.69,5.57,2.89,7.83,5.9Z"
          />
          <path
            fill={isRunning ? "#76db0e" : "#1e4f16"}
            d="M459.01,293.49c-4.14,2.14-7.94,2.26-12.04.65l-1.13-2.04c-2.36-2.75,10.97-6.77,13.72-2.15.51.86.27,2.24-.55,3.54Z"
          />
        </g>

        {/* ── Red ALARM Pilot Lamp (from Chiller.svg lines 1413-1418) ── */}
        <g
          transform="translate(-81.4583, -57.4583) scale(0.416667)"
          style={{
            animation: isAlarm ? "scada-led-fast-blink 0.75s infinite" : "none",
            opacity: isAlarm ? 1 : 0.4,
          }}
        >
          <path
            fill="#c60604"
            d="M448.56,353.51c-3.29-.45-6.22-2.83-7.71-5.65-5.54-13.17,8.47-26.85,20.41-17.55,11.3,8.8,1.62,28.35-12.71,23.19Z"
          />
          <path
            fill="#4f2b2e"
            d="M464.02,330.74c2.18-1.34,3.52,2.03,4.15,4.02,1.17,3.68-.09,10.97-3.12,13.49-3.51,5.81-10.48,8.08-16.48,5.26,4.94.68,10.93-.02,13.92-4.18s4.12-10.38.99-14.59-7.32-6.76-12.16-6.1-8.71,4.27-9.98,9.57l-.49,9.65c-1.75-3.3-3.36-7.19-1.95-11.26,1.79-5.15,6.25-8.88,10.56-9.92,5.47-1.32,10.89.49,14.55,4.07Z"
          />
          <path
            fill={isAlarm ? "#e3433a" : "#591c18"}
            d="M460.07,333.05c.69.62,1.95,1.3,1.85,2.28-.35,3.64-18.81,4.2-17.31-.93.2-.68,1.65-1.16,2.16-1.79,1.66,1.31,2.05,1.4,1.17.29,4.2-1.27,8.24-1.22,12.13.15Z"
          />
          <path
            fill={isAlarm ? "#f59e90" : "#782e28"}
            d="M460.07,333.05l-11.52,1.8c-.69.1-1.83-.21-2.04-.56s.08-1.05.26-1.68c3.7-4.53,9.23-3.21,13.3.44Z"
          />
        </g>

        {/* ═══════════════════════════════════════════════════════════════
            2. High-Tech Mini Digital LCD Display (Right Door at x: 175.5, y: 49.5)
            ═══════════════════════════════════════════════════════════════ */}
        <g clipPath="url(#chillerLcdClip)">
          {/* Active Backlight Display */}
          <rect
            x="175.5"
            y="49.5"
            width="20"
            height="14.5"
            fill={isRunning ? "#0284c7" : "#1e293b"}
            opacity={isRunning ? "0.85" : "0.6"}
          />
          {/* Matrix Scanlines */}
          <line
            x1="175.5"
            y1="53.5"
            x2="195.5"
            y2="53.5"
            stroke="#38bdf8"
            strokeWidth="0.35"
            opacity="0.25"
          />
          <line
            x1="175.5"
            y1="57.5"
            x2="195.5"
            y2="57.5"
            stroke="#38bdf8"
            strokeWidth="0.35"
            opacity="0.25"
          />
          <line
            x1="175.5"
            y1="61.5"
            x2="195.5"
            y2="61.5"
            stroke="#38bdf8"
            strokeWidth="0.35"
            opacity="0.25"
          />

          {isRunning ? (
            <g>
              {/* Primary Chilling Cooling Waveform */}
              <path
                d="M 176 55.5 Q 178.5 52.5 181 55.5 T 186 55.5 T 191 55.5 T 195.5 55.5"
                stroke="#38bdf8"
                strokeWidth="0.9"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.95"
              >
                <animate
                  attributeName="d"
                  values="M 176 55.5 Q 178.5 52.5 181 55.5 T 186 55.5 T 191 55.5 T 195.5 55.5;
                          M 176 55.5 Q 178.5 58.5 181 55.5 T 186 55.5 T 191 55.5 T 195.5 55.5;
                          M 176 55.5 Q 178.5 52.5 181 55.5 T 186 55.5 T 191 55.5 T 195.5 55.5"
                  dur="1.2s"
                  repeatCount="indefinite"
                />
              </path>

              {/* Secondary Harmonic Waveform */}
              <path
                d="M 176 58.5 Q 178.5 60.5 181 58.5 T 186 58.5 T 191 58.5 T 195.5 58.5"
                stroke="#7dd3fc"
                strokeWidth="0.6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.75"
              >
                <animate
                  attributeName="d"
                  values="M 176 58.5 Q 178.5 60.5 181 58.5 T 186 58.5 T 191 58.5 T 195.5 58.5;
                          M 176 58.5 Q 178.5 56.5 181 58.5 T 186 58.5 T 191 58.5 T 195.5 58.5;
                          M 176 58.5 Q 178.5 60.5 181 58.5 T 186 58.5 T 191 58.5 T 195.5 58.5"
                  dur="1.2s"
                  repeatCount="indefinite"
                />
              </path>

              {/* Chilling Activity Level Indicators */}
              <rect x="177" y="61.5" width="2" height="1.8" fill="#38bdf8" opacity="0.95" />
              <rect x="180" y="61.5" width="2" height="1.8" fill="#38bdf8" opacity="0.95" />
              <rect x="183" y="61.5" width="2" height="1.8" fill="#38bdf8" opacity="0.95" />
              <rect x="186" y="61.5" width="2" height="1.8" fill="#38bdf8" opacity="0.75" />
              <rect x="189" y="61.5" width="2" height="1.8" fill="#38bdf8" opacity="0.45" />
              <rect x="192" y="61.5" width="2" height="1.8" fill="#38bdf8" opacity="0.2" />
            </g>
          ) : (
            <g>
              {/* Standby State Baseline */}
              <line
                x1="176"
                y1="56.5"
                x2="195"
                y2="56.5"
                stroke="#64748b"
                strokeWidth="0.6"
                strokeDasharray="1.5 1"
              />
            </g>
          )}
        </g>

        {/* ═══════════════════════════════════════════════════════════════
            3. Dynamic Thermal Water Flow Lines & Continuous Pipe Circuit
            ═══════════════════════════════════════════════════════════════ */}
        {isRunning && (
          <g className="scada-chiller-flows" filter="url(#chillerFlowGlow)">
            {/* ── 1. Hot Water Inflow (Upper Pipe Header at Y: 259.5, Flowing IN towards Left) ── */}
            <g>
              {/* Underglow pipeline trace inside pipe */}
              <line
                x1="535"
                y1="259.5"
                x2="430"
                y2="259.5"
                stroke="#ef4444"
                strokeWidth="6"
                strokeLinecap="round"
                opacity="0.5"
              />
              {/* Animated pulsating flow dashes (Inflow direction: right to left) */}
              <line
                x1="535"
                y1="259.5"
                x2="430"
                y2="259.5"
                stroke="url(#chillerHotFlowGrad)"
                strokeWidth="4.5"
                strokeDasharray="12 8"
                strokeLinecap="round"
                style={{
                  animation: `scada-flow-reverse ${flowDuration} linear infinite`,
                }}
              />
              {/* Inflow Directional Arrow entering chiller header */}
              <polygon
                points="440,255.5 431,259.5 440,263.5"
                fill="#f87171"
                style={{
                  animation: `scada-flow-pulse ${flowDuration} ease-in-out infinite`,
                }}
              />
            </g>

            {/* ── 2. Continuous Internal Evaporator Shell-and-Tube Chilling Loop ── */}
            <g>
              {/* Underglow pipeline trace inside evaporator drum */}
              <path
                d="M 430 259.5 L 50 259.5 Q 32 281.5 50 303.5 L 430 303.5"
                stroke="#0ea5e9"
                strokeWidth="5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.4"
              />
              {/* Animated continuous thermal chilling loop */}
              <path
                d="M 430 259.5 L 50 259.5 Q 32 281.5 50 303.5 L 430 303.5"
                stroke="url(#chillerThermalLoopGrad)"
                strokeWidth="4"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="16 10"
                style={{
                  animation: `scada-flow-reverse ${waveDuration} linear infinite`,
                  opacity: 1,
                }}
              />
            </g>

            {/* ── 3. Cold Chilled Water Outflow (Lower Pipe Header at Y: 303.5, Flowing OUT towards Right) ── */}
            <g>
              {/* Underglow pipeline trace inside pipe */}
              <line
                x1="430"
                y1="303.5"
                x2="535"
                y2="303.5"
                stroke="#0284c7"
                strokeWidth="6"
                strokeLinecap="round"
                opacity="0.5"
              />
              {/* Animated pulsating flow dashes (Outflow direction: left to right) */}
              <line
                x1="430"
                y1="303.5"
                x2="535"
                y2="303.5"
                stroke="url(#chillerColdFlowGrad)"
                strokeWidth="4.5"
                strokeDasharray="12 8"
                strokeLinecap="round"
                style={{
                  animation: `scada-flow-forward ${flowDuration} linear infinite`,
                }}
              />
              {/* Outflow Directional Arrow exiting chiller header */}
              <polygon
                points="525,299.5 534,303.5 525,307.5"
                fill="#38bdf8"
                style={{
                  animation: `scada-flow-pulse ${flowDuration} ease-in-out infinite`,
                }}
              />
            </g>

            {/* ── 4. Upper Compressor Refrigeration Circuit (Inside Top Pipes) ── */}
            <g opacity="0.85">
              {/* Left vertical suction line into valve */}
              <path
                d="M 295 28 L 244.5 28 L 244.5 206"
                stroke="#38bdf8"
                strokeWidth="2.4"
                strokeDasharray="8 6"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  animation: `scada-flow-reverse ${waveDuration} linear infinite`,
                }}
              />
              {/* Right discharge loop from compressor into condenser */}
              <path
                d="M 430 130 L 506 130 L 506 212 L 465 212"
                stroke="#ef4444"
                strokeWidth="2.4"
                strokeDasharray="8 6"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  animation: `scada-flow-forward ${waveDuration} linear infinite`,
                }}
              />
            </g>
          </g>
        )}
      </svg>
    </div>
  );
};

RealisticChiller.propTypes = {
  liveValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  unit: PropTypes.string,
  isRunning: PropTypes.bool,
  isOffline: PropTypes.bool,
  statusColor: PropTypes.string,
  speedScale: PropTypes.number,
  opacity: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
};

export default RealisticChiller;
